import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import DataObjectOutlinedIcon from "@mui/icons-material/DataObjectOutlined";
import { useTranslation } from "react-i18next";
import { useAppCustomStyles } from "../../../../assets/theme/custom/hooks";
import JsonCodeEditor from "../../../../components/JsonCodeEditor";
import { AppButton, AppTextField } from "../../../../components/form";
import type { WidgetQueryLanguage, WidgetSourceType } from "../../../../types/widget";

type AdvancedTab = "mappingHint" | "query";

type Props = {
  sourceType: WidgetSourceType;
  queryLanguage?: WidgetQueryLanguage;
  mappingHintText: string;
  mappingHintJsonError: boolean;
  onMappingHintChange: (value: string) => void;
  dslText: string;
  dslJsonError: boolean;
  onDslChange: (value: string) => void;
  queryBodyText: string;
  onQueryBodyChange: (value: string) => void;
  primaryIndex?: string;
  adaptorReady?: boolean;
  onExecuteQuery: () => Promise<void>;
  executeLoading: boolean;
  executeError: string | null;
  executeResult: string | null;
};

export default function WidgetAdvancedCodePanel({
  sourceType,
  queryLanguage = "OPENSEARCH_DSL",
  mappingHintText,
  mappingHintJsonError,
  onMappingHintChange,
  dslText,
  dslJsonError,
  onDslChange,
  queryBodyText,
  onQueryBodyChange,
  primaryIndex,
  adaptorReady = false,
  onExecuteQuery,
  executeLoading,
  executeError,
  executeResult,
}: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const adaptor = sourceType === "ADAPTOR";
  const [tab, setTab] = useState<AdvancedTab>("query");

  useEffect(() => {
    setTab("query");
  }, [sourceType]);

  const canExecute = adaptor
    ? adaptorReady && queryBodyText.trim().length > 0
    : !dslJsonError && Boolean(primaryIndex?.trim());

  const queryTabLabel = adaptor
    ? queryLanguage === "MONGO"
      ? t("widget.advancedTabMongo")
      : t("widget.advancedTabSql")
    : t("widget.advancedTabDsl");

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_, value: AdvancedTab) => setTab(value)}
        sx={custom.components.widgets.advancedCodeTabs}
      >
        <Tab
          value="mappingHint"
          icon={<AccountTreeOutlinedIcon fontSize="small" />}
          iconPosition="start"
          label={t("widget.advancedTabMappingHint")}
        />
        <Tab
          value="query"
          icon={<DataObjectOutlinedIcon fontSize="small" />}
          iconPosition="start"
          label={queryTabLabel}
        />
      </Tabs>

      {tab === "mappingHint" ? (
        <JsonCodeEditor
          label={t("widget.mappingHint")}
          value={mappingHintText}
          onChange={onMappingHintChange}
          helperText={
            adaptor ? t("widget.mappingHintHelpAdaptor") : t("widget.mappingHintHelp")
          }
          error={mappingHintJsonError}
          height={420}
        />
      ) : (
        <Stack spacing={1.5}>
          {adaptor ? (
            queryLanguage === "MONGO" ? (
              <JsonCodeEditor
                label={t("widget.queryBodyMongo")}
                value={queryBodyText}
                onChange={onQueryBodyChange}
                helperText={t("widget.queryPlaygroundHintAdaptor")}
                height={360}
              />
            ) : (
              <AppTextField
                label={t("widget.queryBodySql")}
                value={queryBodyText}
                onChange={(e) => onQueryBodyChange(e.target.value)}
                helperText={t("widget.queryPlaygroundHintAdaptor")}
                multiline
                minRows={14}
                InputProps={{ sx: { fontFamily: "monospace", fontSize: 13 } }}
              />
            )
          ) : (
            <JsonCodeEditor
              label={t("widget.dslTemplate")}
              value={dslText}
              onChange={onDslChange}
              error={dslJsonError}
              helperText={dslJsonError ? t("widget.invalidJson") : t("widget.dslPlaygroundHint")}
              height={360}
            />
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <AppButton
              variant="contained"
              color="info"
              size="small"
              startIcon={
                executeLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PlayArrowOutlinedIcon />
                )
              }
              disabled={executeLoading || !canExecute}
              onClick={() => onExecuteQuery().catch(console.error)}
            >
              {executeLoading
                ? t("widget.dslPlaygroundRunning")
                : adaptor
                  ? t("widget.queryPlaygroundExecute")
                  : t("widget.dslPlaygroundExecute")}
            </AppButton>
            {adaptor ? (
              adaptorReady ? (
                <Typography variant="caption" color="text.secondary">
                  {t("widget.queryPlaygroundAdaptorReady")}
                </Typography>
              ) : (
                <Typography variant="caption" color="warning.main">
                  {t("widget.queryPlaygroundNeedAdaptor")}
                </Typography>
              )
            ) : primaryIndex ? (
              <Typography variant="caption" color="text.secondary">
                {t("widget.dslPlaygroundIndex", { index: primaryIndex })}
              </Typography>
            ) : (
              <Typography variant="caption" color="warning.main">
                {t("widget.dslPlaygroundNeedSource")}
              </Typography>
            )}
          </Stack>

          {executeError ? <Alert severity="error">{executeError}</Alert> : null}

          {executeResult ? (
            <JsonCodeEditor
              label={
                adaptor ? t("widget.queryPlaygroundResult") : t("widget.dslPlaygroundResult")
              }
              value={executeResult}
              onChange={() => undefined}
              readOnly
              height={320}
            />
          ) : null}
        </Stack>
      )}
    </Box>
  );
}
